class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);

class CartDrawerGiftMessage extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variant_id;
    this.checkbox = this.querySelector(".cart-drawer-gift-message__checkbox");
    this.textarea = this.querySelector(".cart-drawer-gift-message__textarea");
    this.textareaLabel = this.querySelector(
      ".cart-drawer-gift-message__textarea-label"
    );

    this.#setEventListeners();
  }

  #setEventListeners() {
    this.checkbox.addEventListener(
      "change",
      this.#handleCheckboxChange.bind(this)
    );

    this.textarea.addEventListener(
      "input",
      debounce(this.handleMessageUpdate.bind(this), 500)
    );
  }

  async #handleCheckboxChange(event) {
    try {
      if (event.target.checked) {
        this.#openTextarea();
        await this.#addGiftToCart();
      } else {
        this.#closeTextarea();
        await this.#removeGiftFromCart();
      }
    } catch (error) {
      console.error("Error updating gift product:", error);
    }
  }

  #openTextarea() {
    this.textarea.classList.add("cart-drawer-gift-message__textarea--active");
    if (this.textareaLabel) {
      this.textareaLabel.classList.remove(
        "cart-drawer-gift-message__textarea-label--hidden"
      );
    }
  }

  #closeTextarea() {
    this.textarea.classList.remove(
      "cart-drawer-gift-message__textarea--active"
    );
    if (this.textareaLabel) {
      this.textareaLabel.classList.add(
        "cart-drawer-gift-message__textarea-label--hidden"
      );
    }
  }

  /* @todo: combine add and remove to cart functions */
  async #addGiftToCart() {
    const addResponse = await fetch("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        items: [
          {
            id: this.variantId,
            quantity: 1,
          },
        ],
      }),
    });

    if (!addResponse.ok) {
      throw new Error("Failed to add gift to cart");
    }

    const cartStateResponse = await fetch("/cart.js");
    const cartState = await cartStateResponse.json();
    const sectionsResponse = await fetch(
      `?sections=cart-drawer,cart-icon-bubble`
    );
    const sections = await sectionsResponse.json();

    const parsedState = {
      ...cartState,
      sections: sections,
    };

    document.querySelector("cart-drawer").renderContents(parsedState);
  }

  async #removeGiftFromCart() {
    const response = await fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id: this.variantId,
        quantity: 0,
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to remove gift from cart");
    }

    const cartStateResponse = await fetch("/cart.js");
    const cartState = await cartStateResponse.json();
    const sectionsResponse = await fetch(
      `?sections=cart-drawer,cart-icon-bubble`
    );
    const sections = await sectionsResponse.json();

    const parsedState = {
      ...cartState,
      sections: sections,
    };

    document.querySelector("cart-drawer").renderContents(parsedState);
  }

  async handleMessageUpdate() {
    try {
      const message = this.textarea.value;
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      const giftItem = cart.items.find(
        (item) => item.id.toString() === this.variantId
      );
      if (!giftItem) return;

      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: this.variantId,
          quantity: giftItem.quantity,
          properties: {
            Message: message,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update gift message");
      }
    } catch (error) {
      console.error("Error updating gift message:", error);
    }
  }
}

customElements.define("cart-drawer-gift-message", CartDrawerGiftMessage);
